/**
 * <yotpo-product-rating> — Horizon-native Yotpo star-rating element.
 *
 * Fetches the product's bottom-line rating from Yotpo's public widget API and fills in the stars
 * (via per-star linear-gradient offsets), the average score, and the review count. Mirrors the
 * reference theme's behavior; no reference code is copied. Inert if window.YOTPO_ACCESS_TOKEN is
 * unset (the snippet is gated on the same setting, so the element only renders when configured).
 */
// Every star clips its fill through a <mask>, and an SVG mask can only be referenced by id. The
// snippet cannot mint a unique one: `{% increment %}` restarts inside each `{% render %}`, so all
// three renders of a PDP's rating (sticky bar, mobile head, buybox) emitted the SAME id. Duplicate
// ids all resolve to the FIRST match in the document — which on the PDP sits in the `display:none`
// mobile head, giving an empty mask and stars that painted nothing at all. Re-id here instead,
// where uniqueness is guaranteed.
let maskSeq = 0;

/**
 * GOAT (perf): in-flight/settled bottom-line requests, keyed `appKey::productId`.
 *
 * Every `<yotpo-product-rating>` used to fire its own cross-origin request from
 * `connectedCallback`, with no batching and no cache — 24 of them during a homepage load, 12 on a
 * collection page, and three on a single PDP for the SAME product (sticky bar, mobile head,
 * buybox). They all land in the critical window and each response is JSON-parsed and applied on
 * the main thread.
 *
 * Sharing the promise collapses duplicates without changing timing for the first element of each
 * product: the request is issued at exactly the same moment it is today. A failure is evicted so a
 * later element still gets its own attempt, matching today's independent-retry behaviour.
 *
 * @type {Map<string, Promise<any>>}
 */
const bottomlineRequests = new Map();

/**
 * @param {string} appKey
 * @param {string} productId
 * @returns {Promise<any>}
 */
function fetchReviews(appKey, productId) {
  const key = `${appKey}::${productId}`;
  const existing = bottomlineRequests.get(key);
  if (existing) return existing;

  const pending = fetch(`https://api.yotpo.com/v1/widget/${appKey}/products/${productId}/reviews`)
    .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
    .catch((error) => {
      bottomlineRequests.delete(key);
      throw error;
    });

  bottomlineRequests.set(key, pending);
  return pending;
}

class YotpoProductRating extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';
    this.uniquifyMasks();

    /*
     * GOAT (perf): the homepage server-renders a whole second tab of product cards into a `hidden`
     * panel, and `hidden` does not stop `connectedCallback` — so eight ratings fired eight
     * cross-origin requests during the homepage load for cards nobody had asked to see. Defer them
     * until the page has loaded and gone idle, or the panel opens, whichever comes first.
     *
     * Nothing moves as a result: the stars are painted, and the `remove()`-when-empty path has run,
     * while the panel is still hidden, exactly as before the deferral.
     */
    const hiddenPanel = this.closest('[data-tab-panel][hidden]');
    if (hiddenPanel && typeof MutationObserver === 'function') {
      let hydrated = false;
      const hydrateOnce = () => {
        if (hydrated) return;
        hydrated = true;
        observer.disconnect();
        this.hydrate();
      };
      const observer = new MutationObserver(() => {
        if (!hiddenPanel.hasAttribute('hidden')) hydrateOnce();
      });
      observer.observe(hiddenPanel, { attributes: true, attributeFilter: ['hidden'] });

      const warm = () =>
        typeof window.requestIdleCallback === 'function'
          ? window.requestIdleCallback(hydrateOnce, { timeout: 2000 })
          : setTimeout(hydrateOnce, 200);
      if (document.readyState === 'complete') warm();
      else window.addEventListener('load', warm, { once: true });
      return;
    }

    this.hydrate();
  }

  hydrate() {
    const appKey = window.YOTPO_ACCESS_TOKEN;
    const productId = this.getAttribute('data-product-id');
    if (!appKey || !productId) {
      this.remove();
      return;
    }

    fetchReviews(appKey, productId)
      .then((data) => {
        const bottomline = data && data.response && data.response.bottomline;
        if (!bottomline) {
          this.remove();
          return;
        }

        const score = Number(bottomline.average_score) || 0;
        const total = Number(bottomline.total_review) || 0;

        // No reviews: never paint empty stars (reference hides the stars entirely).
        const starsEl = this.querySelector('.yotpo-rating__stars');
        if (score > 0 && total > 0) {
          this.renderStars(score);
        } else if (starsEl) {
          starsEl.hidden = true;
        }

        const scoreEl = this.querySelector('.yotpo-rating__score');
        if (scoreEl) scoreEl.textContent = score && total > 0 ? score.toFixed(1) : '';

        const countEl = this.querySelector('.yotpo-rating__count');
        const countNum = this.querySelector('.yotpo-rating__count-num');
        const emptyEl = this.querySelector('.yotpo-rating__empty');

        // GOAT-54 / min-reviews gate (reference b7356fc): the count renders only at or
        // above the threshold; below a non-zero threshold both the count and the
        // "Be the first to review" text stay hidden.
        const minReviews = parseInt(this.getAttribute('min-review-count'), 10) || 0;

        if (total > 0 && total >= minReviews) {
          if (countNum) countNum.textContent = String(total);
          if (countEl) countEl.hidden = false;
          if (emptyEl) emptyEl.hidden = true;
        } else if (minReviews > 0) {
          if (countEl) countEl.hidden = true;
          if (emptyEl) emptyEl.hidden = true;
        } else if (countEl) {
          countEl.hidden = true;
        }

        // Only unhide when there is something to show: filled stars, or the
        // "(Be the first to review)" empty text in show_count contexts (PDP).
        const emptyVisible = emptyEl && !emptyEl.hidden;
        if (total > 0 || emptyVisible) {
          // The element already occupies its box (see `[data-pending]` in snippets/yotpo-rating
          // .liquid); revealing it must not change layout, so drop the pending flag rather than
          // toggling `hidden`, which would make the row appear from nothing and push the card's
          // title down.
          this.removeAttribute('data-pending');
        } else {
          this.remove();
        }
      })
      .catch(() => this.remove());
  }

  uniquifyMasks() {
    this.querySelectorAll('.yotpo-rating__star').forEach((star) => {
      const mask = star.querySelector('mask');
      const fill = star.querySelector('.yotpo-rating__star-fill');
      if (!mask || !fill) return;
      const id = `yr-mask-${(maskSeq += 1)}`;
      mask.id = id;
      fill.setAttribute('mask', `url(#${id})`);
    });
  }

  renderStars(score) {
    const stars = this.querySelectorAll('.yotpo-rating__star');
    stars.forEach((star, index) => {
      let pct = 0;
      if (score >= index + 1) pct = 100;
      else if (score > index) pct = (score - index) * 100;

      // Each star is an always-painted 1px outline plus a filled copy clipped by this mask rect,
      // matching live. Narrowing the rect reveals the outline behind the star's empty part, which
      // a gradient stop (the previous approach) could not do.
      const mask = star.querySelector('.yotpo-rating__star-mask');
      if (mask) mask.style.width = `${pct}%`;
    });
  }
}

if (!customElements.get('yotpo-product-rating')) {
  customElements.define('yotpo-product-rating', YotpoProductRating);
}
